import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSeoTags, buildWebPageJsonLd } from '../hooks/useSeoTags';
import './AboutPage.css';

const PRINCIPLE_INDEXES = [0, 1, 2, 3] as const;

const AboutPage: React.FC = () => {
  const { t } = useTranslation(['about', 'common']);

  // SEO for /about — uses the about-page lede as the title (it's the H1 anyway)
  // and the kicker/whyNow body as the description so the SERP snippet matches
  // the visible above-the-fold copy. Adds WebPage + BreadcrumbList schema.
  const aboutTitle = t('kicker', { defaultValue: 'About Loopy' });
  const aboutDescription = t('lede');
  const seoTags = useSeoTags({
    title: aboutTitle,
    description: aboutDescription,
    canonical: '/about',
    jsonLd: buildWebPageJsonLd({
      url: '/about',
      name: aboutTitle,
      description: aboutDescription,
      breadcrumbs: [
        { name: 'Loopy', url: '/' },
        { name: aboutTitle, url: '/about' },
      ],
    }),
  });

  return (
    <article className="about-page">
      {seoTags}
      <div className="about-wrap">
        <header className="about-hero">
          <h1 className="about-hero__title">{t('lede')}</h1>
        </header>

        <section className="about-card">
          <h2 className="about-card__heading">{t('whyNow.heading')}</h2>
          <p className="about-card__body">{t('whyNow.body')}</p>
        </section>

        <section className="about-card">
          <h2 className="about-card__heading">{t('principles.heading')}</h2>

          <blockquote className="about-pullquote">
            {t('principles.pullQuote')}
          </blockquote>

          <ol className="about-principles">
            {PRINCIPLE_INDEXES.map((i) => (
              <li key={i} className="about-principles__item">
                <span className="about-principles__num">{t(`principles.items.${i}.number`)}</span>
                <div className="about-principles__text">
                  <h3 className="about-principles__title">{t(`principles.items.${i}.title`)}</h3>
                  <p className="about-principles__body">{t(`principles.items.${i}.body`)}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="about-card">
          <h2 className="about-card__heading">{t('products.heading')}</h2>
          {t('products.body').split('\n\n').map((para, i) => (
            <p key={i} className="about-card__body">{para}</p>
          ))}
        </section>

        <section className="about-close">
          <p className="about-close__body">
            {t('close.bodyBefore')}
            <a href={`mailto:${t('close.email')}`} className="about-close__email">
              {t('close.email')}
            </a>
            {t('close.bodyAfter')}
          </p>
          <Link to="/contact" className="about-close__form-link">
            {t('close.formLink')}
          </Link>
          <Link to="/pricing" className="about-close__cta">
            {t('close.readPricing')}
          </Link>
        </section>

        <footer className="about-credit">{t('credit')}</footer>
      </div>
    </article>
  );
};

export default AboutPage;
